import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ExternalLink } from 'lucide-react';

const PurchasePlatforms = () => {
  const platforms = [
    {
      name: 'Apple Books',
      url: 'https://books.apple.com/us/book/ned-and-nancy-the-first-clue/id6474623788',
      color: 'from-pink-500 to-rose-600',
      hoverGlow: 'rgba(236, 72, 153, 0.4)'
    },
    {
      name: 'Amazon',
      url: 'https://a.co/d/ciDjnhM',
      color: 'from-amber-500 to-orange-600',
      hoverGlow: 'rgba(245, 158, 11, 0.4)'
    },
    {
      name: 'Kobo',
      url: 'https://www.kobo.com/us/en/ebook/ned-and-nancy-the-first-clue',
      color: 'from-teal-500 to-emerald-600',
      hoverGlow: 'rgba(20, 184, 166, 0.4)'
    },
    {
      name: 'Barnes & Noble',
      url: 'https://www.barnesandnoble.com/w/ned-and-nancy-carter-case/1144511802?ean=9781951434212',
      color: 'from-purple-500 to-violet-600',
      hoverGlow: 'rgba(139, 92, 246, 0.4)'
    }
  ];

  return (
    <div className="relative py-24 overflow-hidden bg-[#0a0a1a]">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Animated glow orbs */}
      <motion.div
        className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 60%)',
          filter: 'blur(60px)'
        }}
        animate={{
          x: [-100, 100, -100],
          y: [-50, 50, -50]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-amber-500" />
            <ShoppingBag className="w-8 h-8 text-amber-400" />
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-amber-500" />
          </div>

          <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
            <span className="text-white">Available on </span>
            <span className="gradient-text-gold">These Platforms</span>
          </h2>

          <p className="text-gray-400 text-lg">
            ...and more! Choose your favorite way to read.
          </p>
        </motion.div>

        {/* Platform buttons grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {platforms.map((platform, index) => (
            <motion.a
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: `0 20px 40px ${platform.hoverGlow}`
              }}
              whileTap={{ scale: 0.98 }}
              className="group relative block"
            >
              {/* Gradient border */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${platform.color} p-[2px]`}>
                <div className="w-full h-full bg-[#0f0f2a] rounded-2xl" />
              </div>

              {/* Content */}
              <div className="relative py-5 px-6 text-center">
                <span className="block text-sm text-gray-400 mb-1">BUY ON</span>
                <span className={`block text-xl font-bold bg-gradient-to-r ${platform.color} bg-clip-text text-transparent`}>
                  {platform.name.toUpperCase()}
                </span>

                {/* Arrow icon */}
                <motion.div
                  className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                  initial={{ x: -10 }}
                  whileHover={{ x: 0 }}
                >
                  <ExternalLink className="w-5 h-5 text-gray-400" />
                </motion.div>
              </div>

              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6 }}
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)'
                }}
              />
            </motion.a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PurchasePlatforms;
